/**
 * Session-scoped anonymous ID for pre-authentication tracking.
 *
 * Used by center-auth (and any other surface where the user has no identity
 * yet) to correlate funnel events. Stored in sessionStorage so it survives
 * page reloads within a tab but doesn't persist across sessions — privacy-
 * conservative default.
 *
 * After authentication completes, StatsigClientProvider calls
 * client.updateUserAsync({ userID: realIdentityId }), which lets Statsig
 * stitch the pre-auth and post-auth events for funnel analysis.
 */

const STORAGE_KEY = 'center.analytics.anonymousId';

/**
 * In-memory fallback when sessionStorage is unavailable. Persists for the
 * lifetime of the JS context (until full reload), giving us *some* per-page
 * correlation even in privacy mode / blocked-storage browsers. Strictly
 * better than collapsing every such user into the literal "anonymous"
 * string.
 */
let _inMemoryAnonymousId: string | null = null;

function generateUuid(): string {
  // crypto.randomUUID is available in modern browsers; fall back to a basic
  // pseudo-random ID for older environments / SSR.
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replaceAll(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Returns the current session's anonymous ID, creating one if needed.
 * Always returns a string — falls back to an in-memory ID if sessionStorage
 * is unavailable (privacy mode, third-party-cookie-blocking iframes, etc.).
 *
 * Logs a warning when sessionStorage isn't usable so the failure mode is
 * visible during diagnosis.
 */
export function getOrCreateSessionAnonymousId(): string {
  if (typeof globalThis.sessionStorage === 'undefined') {
    if (!_inMemoryAnonymousId) {
      _inMemoryAnonymousId = generateUuid();
      if (process.env['NODE_ENV'] !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Statsig] sessionStorage unavailable; using in-memory anonymous ID. ' +
            'Cross-page funnel correlation may be limited.'
        );
      }
    }
    return _inMemoryAnonymousId;
  }

  try {
    const existing = globalThis.sessionStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const next = generateUuid();
    globalThis.sessionStorage.setItem(STORAGE_KEY, next);
    return next;
  } catch (err) {
    // Privacy mode / quota errors / iframe storage restrictions.
    if (!_inMemoryAnonymousId) {
      _inMemoryAnonymousId = generateUuid();
      if (process.env['NODE_ENV'] !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Statsig] sessionStorage threw; using in-memory anonymous ID',
          err instanceof Error ? err.message : err
        );
      }
    }
    return _inMemoryAnonymousId;
  }
}
